import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Mail, Phone, Loader2, ArrowLeft, CheckCircle, Send, KeyRound, Eye, EyeOff,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_URL = '/logo.webp';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const TabButton = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 font-heading uppercase tracking-wider text-sm transition-all ${
      active ? 'bg-primary text-black' : 'bg-card text-muted-foreground hover:text-white'
    }`}
    data-testid={testid}
  >
    <Icon className="h-4 w-4" /> {label}
  </button>
);

// ============ EMAIL PATH (unchanged) ============
const EmailResetForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try { await axios.post(`${API_URL}/api/auth/forgot-password`, { email }); } catch { /* silent — enumeration-proof */ }
    setSent(true);
    setLoading(false);
  };

  if (sent) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
        <div className="h-20 w-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-10 w-10 text-green-500" />
        </div>
        <h2 className="font-heading text-3xl text-white uppercase tracking-wider mb-4">
          Check Your <span className="text-primary">Email</span>
        </h2>
        <p className="text-muted-foreground mb-8">
          If an account exists with <span className="text-white">{email}</span>, you'll receive a password reset link shortly.
        </p>
        <Button variant="outline" onClick={() => setSent(false)} className="border-white/20 text-white hover:bg-white/10" data-testid="try-again-btn">
          Try Again
        </Button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <p className="text-muted-foreground">Enter your email and we'll send you a reset link.</p>
      <div className="space-y-2">
        <Label htmlFor="email" className="text-white uppercase tracking-wider text-sm">Email Address</Label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="pl-12 h-14 bg-card border-border text-white focus:border-primary" placeholder="you@example.com" required data-testid="forgot-password-email" />
        </div>
      </div>
      <Button type="submit" className="w-full h-14 bg-primary text-primary-foreground font-heading text-xl tracking-wider uppercase hover:bg-primary/90"
        disabled={loading || !email.trim()} data-testid="forgot-password-submit">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="mr-2 h-5 w-5" /> Send Reset Link</>}
      </Button>
    </form>
  );
};

// ============ SMS PATH ============
const SmsResetForm = () => {
  // step: 'phone' | 'code' | 'password' | 'done'
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Format as (XXX) XXX-XXXX for readability (US-only)
  const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) { toast.error('Enter a 10-digit US phone number'); return; }
    setLoading(true);
    try { await axios.post(`${API_URL}/api/auth/sms-reset/send`, { phone: digits }); } catch { /* silent */ }
    toast.success('If a matching account exists, a 6-digit code is on the way.');
    setStep('code');
    setLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const digits = phone.replace(/\D/g, '');
      const res = await axios.post(`${API_URL}/api/auth/sms-reset/verify`, { phone: digits, code: code.trim() });
      setResetToken(res.data.reset_token);
      setStep('password');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired code');
    }
    setLoading(false);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/sms-reset/complete`, { reset_token: resetToken, new_password: newPassword });
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password');
    }
    setLoading(false);
  };

  if (step === 'done') {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center" data-testid="sms-reset-done">
        <div className="h-20 w-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-10 w-10 text-green-500" />
        </div>
        <h2 className="font-heading text-3xl text-white uppercase tracking-wider mb-4">
          Password <span className="text-primary">Updated</span>
        </h2>
        <p className="text-muted-foreground mb-8">You can now log in with your new password.</p>
        <Link to="/login">
          <Button className="w-full h-14 bg-primary text-primary-foreground font-heading text-xl tracking-wider uppercase hover:bg-primary/90">
            Go to Login
          </Button>
        </Link>
      </motion.div>
    );
  }

  if (step === 'password') {
    return (
      <form onSubmit={handleSetPassword} className="space-y-6" data-testid="sms-reset-password-step">
        <p className="text-muted-foreground">Code verified. Set your new password.</p>
        <div className="space-y-2">
          <Label htmlFor="new-password" className="text-white uppercase tracking-wider text-sm">New Password</Label>
          <div className="relative">
            <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input id="new-password" type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              className="pl-12 pr-12 h-14 bg-card border-border text-white focus:border-primary" placeholder="At least 6 characters" required minLength={6} data-testid="sms-new-password" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white">
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <Button type="submit" className="w-full h-14 bg-primary text-primary-foreground font-heading text-xl tracking-wider uppercase hover:bg-primary/90"
          disabled={loading || newPassword.length < 6} data-testid="sms-set-password-btn">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Set New Password'}
        </Button>
      </form>
    );
  }

  if (step === 'code') {
    return (
      <form onSubmit={handleVerifyCode} className="space-y-6" data-testid="sms-reset-code-step">
        <p className="text-muted-foreground">
          We texted a 6-digit code to <span className="text-white">{phone}</span>. Codes expire in 10 minutes.
        </p>
        <div className="space-y-2">
          <Label htmlFor="sms-code" className="text-white uppercase tracking-wider text-sm">Verification Code</Label>
          <Input id="sms-code" inputMode="numeric" pattern="[0-9]{6}" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="h-14 bg-card border-border text-white focus:border-primary text-center text-2xl tracking-[0.5em] font-heading"
            placeholder="------" maxLength={6} required data-testid="sms-code-input" autoFocus />
        </div>
        <Button type="submit" className="w-full h-14 bg-primary text-primary-foreground font-heading text-xl tracking-wider uppercase hover:bg-primary/90"
          disabled={loading || code.length !== 6} data-testid="sms-verify-code-btn">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Verify Code'}
        </Button>
        <button type="button" onClick={() => { setStep('phone'); setCode(''); }} className="w-full text-muted-foreground hover:text-primary text-sm" data-testid="sms-change-phone-btn">
          ← Use a different phone number
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSendCode} className="space-y-6">
      <p className="text-muted-foreground">Enter the phone number on your account. We'll text you a 6-digit code (US numbers only).</p>
      <div className="space-y-2">
        <Label htmlFor="phone" className="text-white uppercase tracking-wider text-sm">Phone Number</Label>
        <div className="relative">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input id="phone" type="tel" inputMode="tel" value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            className="pl-12 h-14 bg-card border-border text-white focus:border-primary" placeholder="(555) 555-5555" required data-testid="forgot-password-phone" />
        </div>
      </div>
      <Button type="submit" className="w-full h-14 bg-primary text-primary-foreground font-heading text-xl tracking-wider uppercase hover:bg-primary/90"
        disabled={loading || phone.replace(/\D/g, '').length !== 10} data-testid="sms-send-code-btn">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="mr-2 h-5 w-5" /> Text Me a Code</>}
      </Button>
    </form>
  );
};

export const ForgotPasswordPage = () => {
  const [method, setMethod] = useState('email');

  return (
    <div className="min-h-screen bg-black flex" data-testid="forgot-password-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-black to-black" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <img src={LOGO_URL} alt="Ascend & Surpass" className="h-32 w-32 mx-auto mb-8" />
            <h1 className="font-heading text-5xl text-white uppercase tracking-wider mb-4">
              Ascend & <span className="text-primary">Surpass</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-md">Reset your password and get back to crushing your fitness goals.</p>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="w-full max-w-md space-y-8">
          <Link to="/login" className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Login
          </Link>

          <div className="lg:hidden flex justify-center mb-8">
            <img src={LOGO_URL} alt="Ascend & Surpass" className="h-16 w-16" />
          </div>

          <div>
            <h2 className="font-heading text-4xl text-white uppercase tracking-wider mb-2">
              Forgot <span className="text-primary">Password?</span>
            </h2>
          </div>

          {/* Method tabs */}
          <div className="flex gap-1 rounded overflow-hidden border border-border" data-testid="reset-method-tabs">
            <TabButton active={method === 'email'} onClick={() => setMethod('email')} icon={Mail} label="Email" testid="reset-method-email" />
            <TabButton active={method === 'sms'} onClick={() => setMethod('sms')} icon={Phone} label="SMS" testid="reset-method-sms" />
          </div>

          {method === 'email' ? <EmailResetForm /> : <SmsResetForm />}

          <p className="text-center text-muted-foreground">
            Remember your password?{' '}
            <Link to="/login" className="text-primary hover:underline font-semibold">Sign In</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};
